import { useEffect, useReducer, useRef, useState } from "react";
import { isStaleWrite, staleWriteCurrent } from "@repo/api-client";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { ConflictModal } from "./ConflictModal";
import { ImpactDialog } from "./ImpactDialog";
import { ReadOnlyPlanView } from "./ReadOnlyPlanView";
import { PlanCanvas } from "./canvas/PlanCanvas";
import { computeImpact, type ImpactItem } from "./editor/impact";
import { validatePlan } from "./editor/schema";
import { canRedo, canUndo, findTable, findZone, initialState, reducer } from "./editor/state";
import { planFromResponse, planToRequest, type DraftPlan } from "./editor/types";
import {
  floorApi,
  floorKeys,
  floorRejectionOf,
  type FloorPlanResponse,
  type ZoneReducedRejection,
} from "./floorApi";
import { TableInspector } from "./panels/TableInspector";
import { ZonePanel } from "./panels/ZonePanel";
import { PairListPanel } from "./panels/PairListPanel";
import { PolicyPanel } from "./panels/PolicyPanel";

const EMPTY_DRAFT: DraftPlan = {
  mode: "CAPACITY_ONLY",
  totalCovers: null,
  version: 0,
  zones: [],
  tables: [],
  combinables: [],
  policy: { oversizeCap: 2, spreadAcrossZones: false },
};

/**
 * The `/v/:venueId/floor-plan` builder screen (Implementation plan §1/§6). Loads the plan into a
 * buffered reducer draft, edits it element-by-element, and flushes ONE whole-aggregate CAS PUT (FL-6).
 * A 409 opens the conflict modal (reload / re-apply, §D.3); a destructive save opens the impact dialog
 * (R5); an FL-4 standing-capacity rejection surfaces on the zone panel; a `FloorPlanChanged` refetch
 * re-syncs a clean view but only shows a "changed elsewhere" banner on a dirty draft (never clobbers).
 * FLOOR_PLAN off → read-only (EN-2). Route + nav already gate MANAGER.
 */
export function FloorPlanEditor() {
  const { venueId, venue } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = canAccessVenue(claims, venueId, "MANAGER");

  const entitlement = useQuery({
    queryKey: floorKeys.entitlements(venueId),
    queryFn: () => floorApi.getEntitlements(venueId),
    retry: false,
    staleTime: 60_000,
  });
  const planQuery = useQuery({
    queryKey: floorKeys.plan(venueId),
    queryFn: () => floorApi.getPlan(venueId),
  });

  const [state, dispatch] = useReducer(reducer, EMPTY_DRAFT, initialState);
  const serverBaseRef = useRef<DraftPlan>(EMPTY_DRAFT);
  const syncedVersionRef = useRef<number | null>(null);

  const [changedElsewhere, setChangedElsewhere] = useState(false);
  const [conflict, setConflict] = useState<FloorPlanResponse | null>(null);
  const [impact, setImpact] = useState<ImpactItem[] | null>(null);
  const [zoneRejection, setZoneRejection] = useState<ZoneReducedRejection | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reconcile the reducer draft with the server plan. Clean view → adopt fresh state (first load AND
  // SSE-driven refetch); dirty draft whose server version advanced → banner only, never clobber.
  const data = planQuery.data;
  useEffect(() => {
    if (!data) return;
    if (state.dirty) {
      // Genuine SSE-driven conflict detection (dirty draft vs. a server version bump); reworking this
      // into a render-time derivation risks the draft-vs-server reconciliation this editor's
      // CAS-save/conflict-modal flow depends on.
      // oxlint-disable-next-line react/set-state-in-effect -- see comment above.
      if (data.version > state.baseVersion) setChangedElsewhere(true);
      return;
    }
    if (syncedVersionRef.current === data.version) return;
    const fresh = planFromResponse(data);
    serverBaseRef.current = fresh;
    syncedVersionRef.current = data.version;
    setChangedElsewhere(false);
    dispatch({ type: "INIT", plan: fresh });
  }, [data, state.dirty, state.baseVersion]);

  const enabled = !(entitlement.data && entitlement.data.flags?.FLOOR_PLAN === false);

  if (!enabled) {
    return (
      <PageScaffold title={t("floorPlan.title")}>
        <ReadOnlyPlanView plan={planQuery.data ?? null} />
      </PageScaffold>
    );
  }

  if (planQuery.isLoading) {
    return (
      <PageScaffold title={t("floorPlan.title")}>
        <p>{t("floorPlan.loading")}</p>
      </PageScaffold>
    );
  }

  const readOnly = !canEdit;
  const plan = state.plan;
  const validation = validatePlan(plan);
  const activeZone = findZone(plan, state.activeZoneId);
  const selectedTable =
    state.selection.kind === "table" ? findTable(plan, state.selection.id) : undefined;
  const selectedZoneName = selectedTable ? (findZone(plan, selectedTable.zoneId)?.name ?? "") : "";

  async function doSave(versionOverride?: number) {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    setZoneRejection(null);
    try {
      const body = planToRequest(plan);
      if (versionOverride !== undefined) body.version = versionOverride;
      const savedRes = await floorApi.savePlan(venueId, body);
      const fresh = planFromResponse(savedRes);
      serverBaseRef.current = fresh;
      syncedVersionRef.current = savedRes.version;
      dispatch({ type: "SAVED", version: savedRes.version });
      qc.setQueryData(floorKeys.plan(venueId), savedRes);
      setChangedElsewhere(false);
      setImpact(null);
      setSaved(true);
    } catch (err) {
      if (isStaleWrite(err)) {
        const current = staleWriteCurrent<FloorPlanResponse>(err);
        if (current) setConflict(current);
        else setSaveError(t("floorPlan.error.saveFailed"));
      } else {
        const rej = floorRejectionOf(err);
        if (rej?.errorCode === "ZONE_REDUCED_BELOW_COMMITTED") setZoneRejection(rej);
        else if (rej?.errorCode === "INVALID_COMBINATION")
          setSaveError(t("floorPlan.error.invalidCombination"));
        else if (rej?.errorCode === "FLOOR_PLAN_ENTITLEMENT_REQUIRED")
          setSaveError(t("floorPlan.error.entitlement"));
        else setSaveError(t("floorPlan.error.saveFailed"));
      }
    } finally {
      setSaving(false);
    }
  }

  function onSaveClick() {
    if (!validation.ok || readOnly) return;
    const items = computeImpact(serverBaseRef.current, plan);
    if (items.length > 0) {
      setImpact(items);
      return;
    }
    void doSave();
  }

  return (
    <PageScaffold title={`${t("floorPlan.title")} — ${venue.displayName}`}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
        {plan.mode === "CAPACITY_ONLY" && !readOnly ? (
          <div role="note" className={bannerStyle("info")}>
            <p className={css({ fontSize: "sm" })}>{t("floorPlan.upgrade.hint")}</p>
          </div>
        ) : null}

        {changedElsewhere ? (
          <div className={bannerStyle("warn")}>
            <output className={css({ fontSize: "sm" })}>{t("floorPlan.changedElsewhere")}</output>
            <button
              type="button"
              onClick={() => {
                if (data) {
                  const fresh = planFromResponse(data);
                  serverBaseRef.current = fresh;
                  syncedVersionRef.current = data.version;
                  setChangedElsewhere(false);
                  dispatch({ type: "INIT", plan: fresh });
                }
              }}
              className={smallBtn}
            >
              {t("floorPlan.changedElsewhereReload")}
            </button>
          </div>
        ) : null}

        {readOnly ? (
          <p role="note" className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("floorPlan.readOnlyWaiter")}
          </p>
        ) : null}

        {saveError ? (
          <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
            {saveError}
          </p>
        ) : null}

        {/* Toolbar */}
        <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}>
          <button
            type="button"
            disabled={readOnly || !activeZone || activeZone.kind === "STANDING_HEADCOUNT"}
            onClick={() => activeZone && dispatch({ type: "ADD_TABLE", zoneId: activeZone.id })}
            className={smallBtn}
          >
            {t("floorPlan.toolbar.addTable")}
          </button>
          <button
            type="button"
            disabled={readOnly || !canUndo(state)}
            onClick={() => dispatch({ type: "UNDO" })}
            className={smallBtn}
          >
            {t("floorPlan.toolbar.undo")}
          </button>
          <button
            type="button"
            disabled={readOnly || !canRedo(state)}
            onClick={() => dispatch({ type: "REDO" })}
            className={smallBtn}
          >
            {t("floorPlan.toolbar.redo")}
          </button>
          <div className={css({ ml: "auto", display: "flex", gap: "2", alignItems: "center" })}>
            {!validation.ok ? (
              <span role="alert" className={css({ fontSize: "xs", color: "fg.error" })}>
                {validation.message ? t(validation.message) : t("floorPlan.error.invalid")}
              </span>
            ) : null}
            {saved ? (
              <output className={css({ fontSize: "sm", color: "fg.muted" })}>
                {t("floorPlan.saved")}
              </output>
            ) : null}
            <button
              type="button"
              disabled={readOnly || saving || !validation.ok || !state.dirty}
              onClick={onSaveClick}
              className={css({
                px: "4",
                py: "2",
                rounded: "md",
                bg: "colorPalette.default",
                color: "colorPalette.fg",
                _disabled: { opacity: "0.5" },
              })}
            >
              {saving ? t("floorPlan.saving") : t("floorPlan.save")}
            </button>
          </div>
        </div>

        {/* Zone tabs */}
        {plan.zones.length > 0 ? (
          <div
            role="tablist"
            aria-label={t("floorPlan.zoneTabs")}
            className={css({ display: "flex", gap: "1", flexWrap: "wrap" })}
          >
            {plan.zones.map((z) => (
              <button
                key={z.id}
                type="button"
                role="tab"
                aria-selected={z.id === state.activeZoneId}
                onClick={() => dispatch({ type: "SET_ACTIVE_ZONE", zoneId: z.id })}
                className={css({
                  px: "3",
                  py: "1",
                  rounded: "md",
                  fontSize: "sm",
                  borderWidth: "1px",
                  bg: z.id === state.activeZoneId ? "bg.subtle" : "transparent",
                  fontWeight: z.id === state.activeZoneId ? "semibold" : "normal",
                })}
              >
                {z.name || t("floorPlan.zones.untitled")}
              </button>
            ))}
          </div>
        ) : null}

        {/* Dragging tables precisely is impractical on a phone — nudge editors to a larger screen (the
            plan stays fully editable). Shown only below sm. */}
        {!readOnly && (
          <p
            className={css({
              display: { base: "block", sm: "none" },
              fontSize: "sm",
              color: "fg.muted",
              bg: "bg.subtle",
              borderWidth: "1px",
              borderColor: "border.default",
              rounded: "md",
              p: "3",
            })}
          >
            {t("floorPlan.mobileHint")}
          </p>
        )}

        <div
          className={css({
            display: "grid",
            gap: "4",
            gridTemplateColumns: { base: "1fr", lg: "2fr 1fr" },
          })}
        >
          <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
            {activeZone ? (
              <PlanCanvas
                plan={plan}
                zone={activeZone}
                selection={state.selection}
                tool={state.tool}
                linkAnchor={state.linkAnchor}
                disabled={readOnly}
                dispatch={dispatch}
              />
            ) : (
              <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("floorPlan.noZones")}</p>
            )}
            {selectedTable && activeZone ? (
              <TableInspector
                table={selectedTable}
                zoneName={selectedZoneName}
                disabled={readOnly}
                dispatch={dispatch}
              />
            ) : null}
          </div>

          <div className={css({ display: "flex", flexDirection: "column", gap: "5" })}>
            <ZonePanel
              plan={plan}
              activeZoneId={state.activeZoneId}
              disabled={readOnly}
              dispatch={dispatch}
              zoneRejection={zoneRejection}
            />
            <PairListPanel
              plan={plan}
              tool={state.tool}
              linkAnchor={state.linkAnchor}
              disabled={readOnly}
              dispatch={dispatch}
            />
            <PolicyPanel plan={plan} disabled={readOnly} dispatch={dispatch} />
          </div>
        </div>
      </div>

      {impact ? (
        <ImpactDialog
          items={impact}
          onConfirm={() => void doSave()}
          onCancel={() => setImpact(null)}
        />
      ) : null}

      {conflict ? (
        <ConflictModal
          current={conflict}
          onReload={() => {
            const fresh = planFromResponse(conflict);
            serverBaseRef.current = fresh;
            syncedVersionRef.current = conflict.version;
            qc.setQueryData(floorKeys.plan(venueId), conflict);
            setChangedElsewhere(false);
            dispatch({ type: "INIT", plan: fresh });
            setConflict(null);
          }}
          onReapply={() => {
            serverBaseRef.current = planFromResponse(conflict);
            syncedVersionRef.current = conflict.version;
            qc.setQueryData(floorKeys.plan(venueId), conflict);
            dispatch({ type: "REBASE", version: conflict.version });
            const v = conflict.version;
            setConflict(null);
            void doSave(v);
          }}
        />
      ) : null}
    </PageScaffold>
  );
}

function bannerStyle(kind: "info" | "warn") {
  return css({
    p: "3",
    borderWidth: "1px",
    rounded: "md",
    bg: "bg.subtle",
    borderColor: kind === "warn" ? "border.error" : "border.default",
    display: "flex",
    gap: "3",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  });
}

const smallBtn = css({
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "sm",
  _disabled: { opacity: "0.5" },
});
